import { Search } from '@coop/components';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { useSearchOverlayGA4ActionTracking } from '../../../common/tracking/hooks/useSearchOverlayGA4Tracking';
import { useProductSearchStateContext } from '../../../search/hooks/useProductSearchStateContext';
import useReactRouting from '../../hooks/useReactRouting';
import { buildAllProductResultsUrl } from './EcommerceSearchBar.utils';

interface EcommerceSearchBarProps {
    dontNavigateToFullResults?: boolean;
}

const EcommerceSearchBar = forwardRef<HTMLInputElement, EcommerceSearchBarProps>((props, ref) => {
    const { query, queryUsed, isLoading, open, clear, setQuery, show, searchFromPage } =
        useProductSearchStateContext();
    const localRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => localRef.current as HTMLInputElement);

    const { trackEnterAction } = useSearchOverlayGA4ActionTracking(
        query,
        queryUsed,
        searchFromPage,
        isLoading,
    );

    const { push } = useReactRouting();

    useEffect(() => {
        if (show) {
            open();
        }
    }, [show, open]);

    const handleEnterPress = () => {
        trackEnterAction();
        localRef.current?.blur();
        navigateToResultsPage(query, queryUsed);
    };

    const navigateToResultsPage = (_usedQuery?: string, _originalQuery?: string) => {
        if (_usedQuery) {
            const viewAllResultsUrl = buildAllProductResultsUrl(_usedQuery, _originalQuery);
            push(viewAllResultsUrl);
            clear();
        }
    };

    return (
        <Search
            inputRef={localRef}
            submitButtonLabel="Sök vara"
            value={query}
            onChange={setQuery}
            placeholder="Sök bland tusentals varor"
            onEnterPress={(!props.dontNavigateToFullResults && handleEnterPress) || undefined}
            onEscapePress={() => {
                clear();
                localRef.current?.blur();
            }}
            onClear={clear}
            aria-haspopup="dialog"
            aria-label="Sökresultaten kommer att visas medan du skriver"
        />
    );
});

export default EcommerceSearchBar;
